import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import iconChevron from "../../../assets/brand/icon-chevron.svg";
import iconInsight from "../../../assets/brand/icon-insight.svg";
import iconClose from "../../../assets/brand/icon-close.svg";
import iconClose14 from "../../../assets/brand/icon-close-14.svg";
import iconArrowLeft from "../../../assets/brand/icon-arrow-left.svg";
import iconSearch from "../../../assets/brand/icon-search.svg";
import { CAMPAIGN_STATUS, DEAL_STATUS, initialsOf } from "../brandDealUtils";
import { usePhoneTheme } from "../useIsPhone";

// Shared Brand Mode V2 primitives. Every page composes these so spacing,
// radii and type match the Figma components (Button / Badge / Chip /
// MetricCard / Card / InsightBox / Modal / Stepper).

const cx = (...parts) => parts.filter(Boolean).join(" ");

// ---------------------------------------------------------------- Buttons
const BTN_VARIANTS = {
  primary: "bg-bm-orange text-white hover:bg-[#e63d00] disabled:bg-bm-border disabled:text-bm-muted",
  secondary: "bg-white border border-bm-border text-bm-ink hover:bg-bm-subtle disabled:text-bm-tertiary",
  tertiary: "bg-bm-ink text-white hover:bg-black disabled:bg-bm-border disabled:text-bm-muted",
  ghost: "bg-transparent text-bm-ink hover:bg-bm-subtle",
  danger: "bg-transparent text-bm-critical hover:bg-bm-critical/10",
  link: "bg-transparent text-bm-orange underline underline-offset-2",
};

// `sm` is 32px, `md` (the default) 40px, and `lg` the 52px Button · Mobile
// the phone trees use (Figma 1571:651, 2020:1775).
const BTN_SIZES = {
  sm: "h-8 px-[14px] bm-label-xs",
  md: "h-10 px-[18px] bm-label-l",
  lg: "h-[52px] px-6 bm-label-l",
};

// `to` renders a router Link, `href` a plain anchor (for downloads, mailto
// and other sites; target="_blank" gets rel="noopener noreferrer" unless a
// rel is given), anything else a button. All three share the same classes.
export const Btn = ({
  variant = "primary",
  size = "md",
  className = "",
  to,
  href,
  target,
  rel,
  children,
  ...props
}) => {
  const base = cx(
    "inline-flex items-center justify-center rounded-full whitespace-nowrap transition-colors disabled:cursor-not-allowed",
    BTN_SIZES[size] || BTN_SIZES.md,
    BTN_VARIANTS[variant] || BTN_VARIANTS.primary,
    className,
  );
  if (to) {
    return (
      <Link to={to} target={target} rel={rel} className={base} {...props}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
        className={base}
        {...props}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={base} {...props}>
      {children}
    </button>
  );
};

// ------------------------------------------------------------------ Badge
const BADGE_TONES = {
  outline: "border border-bm-strong text-bm-ink bg-white",
  passive: "bg-bm-border text-bm-muted",
  brand: "bg-bm-orange text-white",
  dark: "bg-bm-ink text-white",
  soft: "bg-bm-mutedbg text-bm-n700",
  attention: "bg-bm-attention text-bm-ink",
  critical: "bg-bm-critical text-white",
  onDark: "border border-white/70 text-white bg-transparent",
};

export const Badge = ({ tone = "outline", dot, className = "", children, size = "xs" }) => (
  <span
    className={cx(
      "inline-flex items-center gap-[6px] rounded-full whitespace-nowrap",
      size === "m" ? "h-[26px] px-3 bm-body-m" : "h-[22px] px-[9px] bm-label-xs",
      BADGE_TONES[tone] || BADGE_TONES.outline,
      className,
    )}
  >
    {dot && <span className="h-[7px] w-[7px] rounded-full shrink-0" style={{ background: dot }} />}
    {children}
  </span>
);

export const StatusBadge = ({ status, kind = "deal", className }) => {
  const map = kind === "campaign" ? CAMPAIGN_STATUS : DEAL_STATUS;
  const meta = map[status] || { label: status, tone: "outline" };
  return (
    <Badge tone={meta.tone} dot={meta.dot} className={className}>
      {meta.label}
    </Badge>
  );
};

// ------------------------------------------------------------------- Chip
export const Chip = ({
  active = false,
  brand = false,
  count,
  chevron = false,
  onClick,
  className = "",
  children,
  ...props
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      "inline-flex h-10 items-center gap-2 rounded-full pl-4 pr-[14px] whitespace-nowrap bm-label-l transition-colors",
      active
        ? "bg-bm-n700 text-white"
        : brand
          ? "bg-white border border-bm-orange text-bm-orange"
          : "bg-white border border-bm-border text-bm-ink hover:bg-bm-subtle",
      className,
    )}
    {...props}
  >
    {children}
    {count !== undefined && count !== null && (
      <span className={cx("bm-label-xs", active ? "text-bm-tertiary" : brand ? "text-bm-orange" : "text-bm-tertiary")}>
        {count}
      </span>
    )}
    {chevron && (
      <img
        src={iconChevron}
        alt=""
        className={cx("h-[7px] w-3", brand && "brand-chevron")}
        style={brand ? { filter: "invert(41%) sepia(96%) saturate(3765%) hue-rotate(6deg) brightness(101%) contrast(104%)" } : undefined}
      />
    )}
  </button>
);

// ---------------------------------------------------------------- Layouts
export const Overline = ({ className = "", children, brand = false }) => (
  <p className={cx("bm-overline", brand && "!text-bm-orange", className)}>{children}</p>
);

export const Card = ({ className = "", children, padding = "p-6", as: Tag = "div", ...props }) => (
  <Tag className={cx("rounded-[12px] border border-bm-border bg-white", padding, className)} {...props}>
    {children}
  </Tag>
);

export const MetricCard = ({ label, value, sub, muted = false, className = "" }) => (
  <div
    className={cx(
      "flex min-w-0 flex-1 flex-col gap-2 rounded-[12px] px-6 py-5",
      muted ? "bg-bm-mutedbg" : "border border-bm-border bg-white",
      className,
    )}
  >
    <Overline>{label}</Overline>
    <div className="bm-num-l text-bm-ink">{value}</div>
    {sub && <div className="bm-body-s text-bm-muted">{sub}</div>}
  </div>
);

export const Tile = ({ label, value, sub, accent = false, className = "" }) => (
  <div className={cx("flex min-w-0 flex-1 flex-col gap-1 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px]", className)}>
    <Overline>{label}</Overline>
    <div className={cx("bm-num-m", accent ? "text-bm-orange" : "text-bm-ink")}>{value}</div>
    {sub && <div className="bm-body-s text-bm-muted">{sub}</div>}
  </div>
);

// `compact` is the phone box (Figma 2019:1753): tighter padding, a 12px gap
// instead of the body margin, and no tiles (phone trees move them into a
// StatList card of their own).
export const InsightBox = ({ title, subtitle, children, tiles, className = "", compact = false }) => {
  const head = (
    <div className="flex items-center gap-[10px]">
      <img src={iconInsight} alt="" className="h-6 w-6" />
      <span className="bm-h-s text-bm-ink">{title}</span>
    </div>
  );
  // An optional subtitle sits under the title, in line with its text.
  const heading = subtitle ? (
    <div className="flex flex-col gap-[2px]">
      {head}
      <p className={cx("pl-[34px] text-bm-muted", compact ? "bm-body-xs" : "bm-body-s")}>{subtitle}</p>
    </div>
  ) : (
    head
  );
  if (compact) {
    return (
      <div className={cx("flex flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-6 py-[22px]", className)}>
        {heading}
        {children && <div className="bm-body-m text-bm-muted">{children}</div>}
      </div>
    );
  }
  return (
    <div className={cx("rounded-[8px] border border-bm-border bg-white px-[30px] py-7", className)}>
      {heading}
      {children && <div className="mt-[18px] bm-body-m text-bm-muted">{children}</div>}
      {tiles?.length ? (
        <div className="mt-[18px] flex flex-wrap gap-[14px]">
          {tiles.map((t) => (
            <Tile key={t.label} label={t.label} value={t.value} sub={t.sub} className="max-w-[210px]" />
          ))}
        </div>
      ) : null}
    </div>
  );
};

// ----------------------------------------------------------------- Avatar
const AVATAR_TONES = {
  brand: "bg-bm-orange text-white",
  outline: "bg-white border border-bm-orange text-bm-orange",
  muted: "bg-bm-mutedbg text-bm-muted",
  dark: "bg-bm-ink text-white rounded-[8px]",
};

export const Avatar = ({ name, surname, initials, src, size = 32, tone = "outline", square = false, className = "" }) => {
  const [failed, setFailed] = useState(false);
  const text = initials || initialsOf(name, surname);
  const dim = { width: size, height: size };
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        style={dim}
        onError={() => setFailed(true)}
        className={cx("shrink-0 object-cover", square ? "rounded-[8px]" : "rounded-full", className)}
      />
    );
  }
  return (
    <span
      style={{ ...dim, fontSize: Math.max(9, Math.round(size * 0.32)) }}
      className={cx(
        "inline-flex shrink-0 items-center justify-center font-inter font-bold tracking-[0.2px]",
        square ? "rounded-[8px]" : "rounded-full",
        AVATAR_TONES[tone] || AVATAR_TONES.outline,
        className,
      )}
    >
      {text}
    </span>
  );
};

// ---------------------------------------------------------- Segmented ctl
// Each option is a toggle button; aria-pressed marks the selected one.
// `capitalize={false}` keeps labels as written (acronyms, sentence case).
export const SegmentedControl = ({ value, onChange, options, className = "", capitalize = true }) => (
  <div className={cx("inline-flex h-10 items-center rounded-full bg-bm-mutedbg p-[3px]", className)}>
    {options.map((opt) => {
      const v = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      const active = v === value;
      return (
        <button
          key={v}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(v)}
          className={cx(
            "h-full rounded-full px-4 bm-label-xs",
            capitalize && "capitalize",
            "transition-colors",
            active ? "bg-bm-n700 text-white" : "text-bm-muted hover:text-bm-ink",
          )}
        >
          {label}
        </button>
      );
    })}
  </div>
);

// ------------------------------------------------------------------ Forms
export const Field = ({ label, hint, className = "", children, right }) => (
  <label className={cx("flex min-w-0 flex-col gap-[6px]", className)}>
    {(label || right) && (
      <span className="flex items-center justify-between">
        <span className="bm-label-l text-bm-ink">{label}</span>
        {right}
      </span>
    )}
    {children}
    {hint && <span className="bm-body-s text-bm-muted">{hint}</span>}
  </label>
);

export const Input = ({ className = "", prefix, ...props }) =>
  prefix ? (
    <span className="flex h-10 items-center gap-[3px] rounded-[8px] border border-bm-border bg-white px-[14px] focus-within:border-bm-orange">
      <span className="bm-body-m text-bm-muted">{prefix}</span>
      <input className={cx("bm-bare min-w-0 flex-1 bg-transparent bm-body-m text-bm-ink outline-none placeholder:text-bm-placeholder", className)} {...props} />
    </span>
  ) : (
    <input className={cx("bm-input", className)} {...props} />
  );

export const TextArea = ({ className = "", ...props }) => (
  <textarea className={cx("bm-input bm-textarea", className)} {...props} />
);

export const Select = ({ className = "", children, brand = false, ...props }) => (
  <select className={cx("bm-input bm-select", brand && "bm-select--brand", className)} {...props}>
    {children}
  </select>
);

export const Toggle = ({ on, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={() => onChange(!on)}
    className="bm-toggle"
    data-on={on ? "true" : "false"}
  />
);

// `indeterminate` (a "select all" with only some rows picked) shows a dash
// and reports aria-checked="mixed"; a click then asks for !checked.
export const Checkbox = ({
  checked,
  onChange,
  label,
  className = "",
  indeterminate = false,
  disabled,
  id,
  name,
  "aria-label": ariaLabel,
}) => {
  const mixed = Boolean(indeterminate);
  return (
    <button
      type="button"
      role="checkbox"
      id={id}
      name={name}
      aria-label={ariaLabel}
      aria-checked={mixed ? "mixed" : checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx("inline-flex items-center gap-[10px] text-left disabled:cursor-not-allowed disabled:opacity-50", className)}
    >
      <span className="bm-check" data-on={checked || mixed ? "true" : "false"}>
        {mixed ? (
          <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
            <path d="M1 1h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        ) : (
          checked && (
            <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
              <path d="M1 4.5l3 3L10 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )
        )}
      </span>
      {label && <span className="bm-body-m text-bm-ink">{label}</span>}
    </button>
  );
};

export const SearchBox = ({
  value,
  onChange,
  placeholder,
  className = "",
  subtle = false,
  autoFocus = false,
  id,
  name,
  onKeyDown,
  "aria-label": ariaLabel,
}) => (
  <span
    className={cx(
      "flex h-10 items-center gap-2 rounded-[8px] border border-bm-border px-[14px] focus-within:border-bm-orange",
      subtle ? "bg-bm-subtle" : "bg-white",
      className,
    )}
  >
    <img src={iconSearch} alt="" className="h-4 w-4 opacity-70" />
    <input
      id={id}
      name={name}
      aria-label={ariaLabel}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className="bm-bare min-w-0 flex-1 bg-transparent bm-body-m text-bm-ink outline-none placeholder:text-bm-placeholder"
    />
  </span>
);

// ------------------------------------------------------------- Navigation
export const BackLink = ({ to, children, onClick }) => {
  const inner = (
    <>
      <img src={iconArrowLeft} alt="" className="h-4 w-4 opacity-70" />
      <span className="bm-label-l text-bm-muted">{children}</span>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="inline-flex items-center gap-1 hover:opacity-80">
        {inner}
      </button>
    );
  }
  return (
    <Link to={to} className="inline-flex items-center gap-1 hover:opacity-80">
      {inner}
    </Link>
  );
};

// `align="center"` centres the actions on the title block (Coach Mode
// headers, Figma 1371:1352); the default keeps them on the baseline. The
// actions drop under the title when they do not fit beside it, and wrap onto
// more lines on a narrow screen instead of running off the edge.
//
// Inside the phone theme (pages with no phone head of their own: billing
// and password) it takes the phone page head of DESIGN.md 4.2 / 5.6: no
// mode eyebrow (a brand-coloured flow overline stays), the title right
// under the shell's padding, a Body/S subtitle and the actions below it.
export const PageHeader = (props) => (usePhoneTheme() ? <PhonePageHeader {...props} /> : <DesktopPageHeader {...props} />);

const PhonePageHeader = ({ eyebrow = "Brand Mode", title, subtitle, actions, eyebrowBrand = false, info }) => (
  <div className="flex flex-col gap-[18px] break-words pb-[18px]">
    <div className={cx("flex flex-col", eyebrowBrand ? "gap-[6px]" : "gap-1")}>
      {eyebrowBrand && <Overline brand>{eyebrow}</Overline>}
      <div className="flex items-center gap-2">
        <h1 className="bm-h-l text-bm-ink">{title}</h1>
        {info}
      </div>
      {subtitle && <p className="bm-body-s text-bm-muted">{subtitle}</p>}
    </div>
    {actions && <div className="flex min-w-0 flex-wrap items-center gap-3">{actions}</div>}
  </div>
);

const DesktopPageHeader = ({ eyebrow = "Brand Mode", title, subtitle, actions, eyebrowBrand = false, info, align = "end" }) => (
  <div className={cx("flex flex-wrap justify-between py-[34px]", align === "center" ? "items-center gap-6" : "items-end gap-4")}>
    <div className={cx("flex flex-col gap-[6px]", align === "center" ? "min-w-[280px] flex-1 basis-0" : "min-w-0")}>
      <Overline brand={eyebrowBrand}>{eyebrow}</Overline>
      <div className="flex items-center gap-2">
        <h1 className="bm-h-l text-bm-ink">{title}</h1>
        {info}
      </div>
      {subtitle && <p className="max-w-[760px] bm-body-l text-bm-muted">{subtitle}</p>}
    </div>
    {actions && <div className="flex min-w-0 flex-wrap items-center gap-3">{actions}</div>}
  </div>
);

export const CampaignContextBar = ({ campaign, right, linkLabel = "View campaign", linkTo }) => (
  <Card padding="px-6 py-4" className="flex flex-wrap items-center justify-between gap-3">
    <div className="flex flex-col gap-[2px]">
      <Overline>Campaign</Overline>
      <div className="flex items-center gap-2">
        <span className="bm-h-s text-bm-ink">{campaign?.name}</span>
        {campaign?.status && <StatusBadge status={campaign.status} kind="campaign" />}
      </div>
    </div>
    <div className="flex items-center gap-6 bm-body-m text-bm-muted">
      {right}
      {linkTo && (
        <Link to={linkTo} className="bm-label-l text-bm-orange underline underline-offset-2">
          {linkLabel}
        </Link>
      )}
    </div>
  </Card>
);

export const ActionBar = ({ left, right, subtle = false, className = "" }) => (
  <div
    className={cx(
      "flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-bm-border p-6",
      subtle ? "bg-bm-subtle" : "bg-white",
      className,
    )}
  >
    <div className="flex flex-wrap items-center gap-3">{left}</div>
    <div className="flex flex-wrap items-center gap-6">{right}</div>
  </div>
);

// ------------------------------------------------------------------ Modal
// Open BmModals, bottom first. Only the last one (the topmost) answers
// Escape and keeps Tab inside itself, so a ConfirmModal opened over another
// modal closes alone. One document listener serves the whole stack.
const modalStack = [];

const onStackKeyDown = (e) => {
  const top = modalStack[modalStack.length - 1];
  if (top) top.onKeyDown(e);
};

const pushModal = (entry) => {
  if (!modalStack.length) document.addEventListener("keydown", onStackKeyDown);
  // A modal rendered inside another's body mounts before its parent does;
  // keep the parent below it.
  const inner = modalStack.findIndex((m) => entry.node && m.node && entry.node.contains(m.node));
  if (inner === -1) modalStack.push(entry);
  else modalStack.splice(inner, 0, entry);
};

const removeModal = (entry) => {
  const i = modalStack.indexOf(entry);
  if (i !== -1) modalStack.splice(i, 1);
  if (!modalStack.length) document.removeEventListener("keydown", onStackKeyDown);
};

const FOCUSABLE = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "iframe",
  "[contenteditable]:not([contenteditable='false'])",
  "[tabindex]",
].join(",");

// Tabbable elements inside `root`, in document order.
const focusablesIn = (root) => {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    const tabindex = el.getAttribute("tabindex");
    if (tabindex !== null && Number(tabindex) < 0) return false;
    if (el.closest("[hidden], [inert]")) return false;
    if (typeof el.checkVisibility === "function" && !el.checkVisibility()) return false;
    return true;
  });
};

// b comes after a in the document.
const isAfter = (a, b) => Boolean(a.compareDocumentPosition(b) & 4);

// Each modal's backdrop, mapped to the ref holding where it hands focus back.
// Kept after close so a chain of openers can still be followed.
const returnTargets = new WeakMap();

// The opener, or when it left the page inside a modal that closed too (a
// confirm that closed the modal it was opened from), that modal's opener.
const resolveReturn = (start) => {
  let el = start;
  for (let hops = 0; el && !el.isConnected && hops < 10; hops += 1) {
    let node = el;
    while (node && !returnTargets.has(node)) node = node.parentNode;
    el = node ? returnTargets.get(node).current : null;
  }
  return el;
};

const canTakeFocus = (el) =>
  Boolean(el && el !== document.body && el.isConnected && !el.disabled && typeof el.focus === "function");

// Runs once the commit that closed a modal is done. A modal still open must
// hold focus (the opener may have been a row this same commit removed);
// with none left, focus goes back along the opener chain if it was lost.
const settleFocus = (from) => {
  const active = document.activeElement;
  const top = modalStack[modalStack.length - 1];
  if (top) {
    if (!top.node || top.node.contains(active)) return;
    const back = resolveReturn(from);
    if (canTakeFocus(back) && top.node.contains(back)) back.focus();
    else top.dialog?.focus();
    return;
  }
  if (active && active !== document.body) return;
  const back = resolveReturn(from);
  if (canTakeFocus(back)) back.focus();
};

const hasTitle = (title) => title !== undefined && title !== null && title !== false && title !== "";

// Sheets and full-screen modals stop the page behind from scrolling. The
// lock is counted, so a sheet opened over another one leaves the page locked
// until the last of them closes, and then puts back whatever was there.
let pageScrollLocks = 0;
let pageOverflowBefore = "";

const lockPageScroll = () => {
  if (typeof document === "undefined" || !document.body) return () => {};
  if (pageScrollLocks === 0) {
    pageOverflowBefore = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  pageScrollLocks += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pageScrollLocks -= 1;
    if (pageScrollLocks === 0) document.body.style.overflow = pageOverflowBefore;
  };
};

// "auto" follows the phone theme: `phonePresentation` (a sheet unless it
// asks for full screen) inside the Athlete, Coach and sign-up shells on a
// phone, today's centred dialog everywhere else.
const resolvePresentation = (presentation, phonePresentation, phoneTheme) => {
  if (presentation === "dialog" || presentation === "sheet" || presentation === "fullscreen") return presentation;
  if (!phoneTheme) return "dialog";
  return phonePresentation === "fullscreen" ? "fullscreen" : "sheet";
};

const MODAL_OVERLAY = {
  dialog: "fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-10",
  // Scrim 2087:1815 / 2410:1259; the sheet sits on the bottom edge.
  sheet: "fixed inset-0 z-[60] flex items-end justify-center bg-black/40",
  fullscreen: "fixed inset-0 z-[60] bg-white",
};

const MODAL_DIALOG = {
  dialog: "bm-root bm-fade-in bm-modal-shadow w-full overflow-hidden rounded-[16px] bg-white outline-none",
  // Sheet 2087:1816: as tall as its content, never closer than 24px to the
  // top; the body scrolls inside it.
  sheet: "bm-root bm-sheet-in flex max-h-[calc(100%-24px)] w-full flex-col overflow-hidden rounded-t-[24px] bg-white outline-none",
  // 2421:2393: fills the overlay, which is pinned to the viewport.
  fullscreen: "bm-root flex h-full w-full flex-col bg-white outline-none",
};

// Stacked footers (action bar 2421:2595, sheet 2087:1816): every action is a
// 52px full-width pill, one under the other.
const STACKED_FOOTER_ITEMS = "[&>*]:!h-[52px] [&>*]:!w-full [&>*]:!max-w-none [&>*]:!flex-none [&>*]:!mx-0 [&>*]:!px-6";

// Painted through a mask so the 14px sheet X reads as the ink 18px close of
// the full-screen header (MobileShell 1767:735).
const maskStyle = (src, size) => ({
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskSize: `${size}px ${size}px`,
  maskSize: `${size}px ${size}px`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
});

// The shared dialog. The title is the dialog's accessible name (an h2). On
// open, focus goes to `initialFocusRef` when given, else stays on a child
// that took it with autoFocus, else the first control in the body or footer,
// else the dialog itself. Tab and Shift+Tab stay inside, and focus goes back
// to where it was once the dialog closes (when that control is gone, to the
// modal still open below, if any). `dismissible={false}` ignores
// Escape and backdrop clicks; the Close button and the footer still work.
//
// `presentation` picks the markup; the stack, focus and Escape handling
// above are shared by all three:
//  - "dialog": the centred desktop dialog.
//  - "sheet": a bottom sheet (SheetHeader · Mobile 2082:1015) with a
//    grabber, the title beside a 28px close and the footer actions stacked.
//  - "fullscreen": a full-screen page (2421:2393) with the close X on the
//    left of a centred title (pass a string title) and a sticky action bar.
//  - "auto" (default): `phonePresentation` ("sheet" or "fullscreen") inside
//    the phone theme, else "dialog".
// Stacked footers show the last child on top (the primary action, by the
// "Cancel, then Confirm" convention); `phoneFooterOrder="natural"` keeps a
// footer written primary first in its own order. Sheets and full-screen
// modals lock the page scroll while open.
export const BmModal = ({
  title,
  onClose,
  children,
  footer,
  width = 560,
  className = "",
  dismissible = true,
  initialFocusRef,
  presentation = "auto",
  phonePresentation = "sheet",
  phoneFooterOrder = "reverse",
}) => {
  const phoneTheme = usePhoneTheme();
  const mode = resolvePresentation(presentation, phonePresentation, phoneTheme);
  const titleId = useId();
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  const initialFocusRefRef = useRef(initialFocusRef);
  // Read while rendering: by commit time a child's autoFocus has moved it.
  const [openedFrom] = useState(() => (typeof document === "undefined" ? null : document.activeElement));
  const returnToRef = useRef(openedFrom);
  const entryRef = useRef(null);

  useLayoutEffect(() => {
    onCloseRef.current = onClose;
    dismissibleRef.current = dismissible;
    initialFocusRefRef.current = initialFocusRef;
  });

  if (!entryRef.current) {
    entryRef.current = {
      node: null,
      onKeyDown: (e) => {
        if (e.key === "Escape") {
          if (dismissibleRef.current) onCloseRef.current?.();
          return;
        }
        // A control that already used Tab (a tag input adding a tag) keeps it.
        if (e.key !== "Tab" || e.defaultPrevented) return;
        const dialog = dialogRef.current;
        if (!dialog) return;
        const items = focusablesIn(dialog);
        const active = document.activeElement;
        if (!items.length) {
          e.preventDefault();
          dialog.focus();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const outside = !active || active === dialog || !dialog.contains(active);
        if (e.shiftKey) {
          if (outside || !items.some((el) => isAfter(el, active))) {
            e.preventDefault();
            last.focus();
          }
        } else if (outside || !items.some((el) => isAfter(active, el))) {
          e.preventDefault();
          first.focus();
        }
      },
    };
  }

  useLayoutEffect(() => {
    const entry = entryRef.current;
    const overlay = overlayRef.current;
    const dialog = dialogRef.current;
    entry.node = overlay;
    entry.dialog = dialog;
    returnTargets.set(overlay, returnToRef);
    pushModal(entry);

    // Opened in the same commit that closed another modal: the control
    // that had focus went with it, so take the one that modal handed back.
    const opener = returnToRef.current;
    if ((!opener || !opener.isConnected) && !overlay.contains(document.activeElement)) {
      returnToRef.current = document.activeElement;
    }

    const wanted = initialFocusRefRef.current?.current;
    if (wanted && typeof wanted.focus === "function") wanted.focus();
    if (!wanted || document.activeElement !== wanted) {
      if (!dialog.contains(document.activeElement)) {
        const firstControl = focusablesIn(bodyRef.current)[0] || focusablesIn(footerRef.current)[0];
        (firstControl || dialog).focus();
      }
    }

    return () => {
      removeModal(entry);
      const active = document.activeElement;
      const focusWasInside = !active || active === document.body || overlay.contains(active);
      if (!focusWasInside) return;
      const back = resolveReturn(returnToRef.current);
      if (canTakeFocus(back)) back.focus();
      queueMicrotask(() => settleFocus(returnToRef.current));
    };
  }, []);

  const locksPage = mode !== "dialog";
  useLayoutEffect(() => {
    if (!locksPage) return undefined;
    return lockPageScroll();
  }, [locksPage]);

  const titled = hasTitle(title);
  const sheet = mode === "sheet";
  const stacked = mode !== "dialog";

  // The overlay, dialog, header, body and footer keep their places in every
  // presentation, so a modal that changes presentation while open (a phone
  // turned to landscape) keeps its body and whatever was typed in it.
  let header;
  if (sheet) {
    header = (
      <div className="flex shrink-0 flex-col items-center gap-3 px-6 pb-1 pt-2">
        <div aria-hidden="true" className="h-[5px] w-9 shrink-0 rounded-full bg-bm-strong" />
        <div className="flex w-full items-center gap-3">
          {titled ? (
            <h2 id={titleId} className="min-w-0 flex-1 bm-h-m text-bm-ink">
              {title}
            </h2>
          ) : (
            <div className="min-w-0 flex-1" />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] hover:bg-bm-subtle"
          >
            <img src={iconClose14} alt="" className="h-[14px] w-[14px]" />
          </button>
        </div>
      </div>
    );
  } else if (stacked) {
    header = (
      <div className="grid shrink-0 grid-cols-[minmax(40px,1fr)_auto_minmax(40px,1fr)] items-center bg-white px-4 pb-[10px] pt-[6px]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-10 w-10 items-center justify-center rounded-[8px] hover:bg-bm-subtle"
        >
          <span aria-hidden="true" className="inline-block h-[18px] w-[18px] shrink-0 bg-bm-ink" style={maskStyle(iconClose14, 18)} />
        </button>
        {titled && (
          <h2 id={titleId} className="min-w-0 truncate text-center bm-label-l text-bm-muted">
            {title}
          </h2>
        )}
      </div>
    );
  } else {
    header = (
      <div className="flex items-start justify-between gap-4 border-b border-bm-border pb-[18px] pl-7 pr-6 pt-[22px]">
        {titled ? (
          <h2 id={titleId} className="bm-h-m text-bm-ink">
            {title}
          </h2>
        ) : (
          <div />
        )}
        <button type="button" onClick={onClose} aria-label="Close" className="mt-1 h-4 w-4 shrink-0 opacity-70 hover:opacity-100">
          <img src={iconClose} alt="" className="h-4 w-4" />
        </button>
      </div>
    );
  }

  let bodyClass = "px-7 pb-6 pt-5";
  if (sheet) {
    bodyClass = cx(
      "min-h-0 flex-auto overflow-y-auto overscroll-contain px-6 pt-4",
      !footer && "pb-[max(34px,env(safe-area-inset-bottom))]",
    );
  } else if (stacked) {
    bodyClass = cx(
      "min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-5",
      footer ? "pb-8" : "pb-[max(32px,env(safe-area-inset-bottom))]",
    );
  }

  const footerClass = stacked
    ? cx(
        "flex shrink-0 gap-3 px-6 pt-4",
        phoneFooterOrder === "natural" ? "flex-col" : "flex-col-reverse",
        STACKED_FOOTER_ITEMS,
        sheet
          ? "pb-[max(34px,env(safe-area-inset-bottom))]"
          : "border-t border-bm-mutedbg bg-white pb-[max(27px,env(safe-area-inset-bottom))]",
      )
    : "flex items-center gap-3 border-t border-bm-border px-7 pb-5 pt-4";

  return (
    <div
      ref={overlayRef}
      className={MODAL_OVERLAY[mode]}
      onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose?.()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titled ? titleId : undefined}
        tabIndex={-1}
        data-presentation={stacked ? mode : undefined}
        style={stacked ? undefined : { maxWidth: width }}
        className={cx(MODAL_DIALOG[mode], className)}
      >
        {header}
        <div ref={bodyRef} className={bodyClass}>
          {children}
        </div>
        {footer && (
          <div ref={footerRef} className={footerClass}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// A modal that is always a bottom sheet, for trees that only render on
// phones (the edit hub's add sheets, Add a platform).
export const BmSheet = (props) => <BmModal presentation="sheet" {...props} />;

// Two-step confirmation (the design calls for "2 step verification" on
// archive / cancel): the confirm button only arms after the acknowledgment.
// `requireAck={false}` drops the checkbox for a plain yes/no question.
// `presentation` / `phonePresentation` go to BmModal (a sheet on phones).
export const ConfirmModal = ({
  title,
  body,
  ackLabel = "I understand this cannot be undone.",
  confirmLabel = "Confirm",
  cancelLabel = "Keep it",
  tone = "primary",
  onConfirm,
  onClose,
  busy = false,
  requireAck = true,
  dismissible = true,
  presentation,
  phonePresentation,
}) => {
  const [ack, setAck] = useState(false);
  return (
    <BmModal
      title={title}
      onClose={onClose}
      dismissible={dismissible}
      presentation={presentation}
      phonePresentation={phonePresentation}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            {cancelLabel}
          </Btn>
          <Btn
            variant={tone === "danger" ? "tertiary" : "primary"}
            className={cx("flex-1", tone === "danger" && "!bg-bm-critical hover:!bg-[#c41d14]")}
            disabled={(requireAck && !ack) || busy}
            onClick={onConfirm}
          >
            {busy ? "Working..." : confirmLabel}
          </Btn>
        </>
      }
    >
      <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px] bm-body-m text-bm-muted">{body}</div>
      {requireAck && (
        <div className="mt-4">
          <Checkbox checked={ack} onChange={setAck} label={ackLabel} />
        </div>
      )}
    </BmModal>
  );
};

// ---------------------------------------------------------------- Steppers
const DEAL_STEPS = ["Open", "In Contract", "In Progress", "Completed"];

// The phone deal progress (Tracking 2025:1515): the same four steps as a
// list, one row each, with no card of its own (the caller's card carries the
// "Deal progress" title). The list numbers the steps for screen readers and
// the current one is marked aria-current="step".
const DealStepperList = ({ current, className }) => (
  <ol className={cx("flex flex-col", className)}>
    {DEAL_STEPS.map((label, i) => {
      const n = i + 1;
      const done = n < current;
      const active = n === current;
      return (
        <li key={label} aria-current={active ? "step" : undefined} className="flex items-center gap-3 py-[6px]">
          <span
            aria-hidden="true"
            className={cx(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bm-label-xs",
              done ? "bg-bm-placeholder text-white" : active ? "bg-bm-ink text-white" : "bg-bm-mutedbg text-bm-tertiary",
            )}
          >
            {done ? "✓" : n}
          </span>
          <span className={cx("bm-label-l", done || active ? "text-bm-ink" : "text-bm-muted")}>{label}</span>
        </li>
      );
    })}
  </ol>
);

// 4-step "Campaign lifecycle" strip (deal pages). `current` is 1-based.
const DealStepperCard = ({ current, className }) => (
  <Card className={className}>
    <Overline>Campaign lifecycle</Overline>
    <div className="mt-6 flex items-center">
      {DEAL_STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        const last = i === DEAL_STEPS.length - 1;
        return (
          <div key={label} className={cx("relative flex items-center", last ? "shrink-0" : "min-w-0 flex-1")}>
            <span
              className={cx(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bm-label-xs",
                done ? "bg-bm-placeholder text-white" : active ? "bg-bm-ink text-white" : "bg-bm-mutedbg text-bm-tertiary",
              )}
            >
              {done ? "✓" : n}
            </span>
            {!last && (
              <span className={cx("mx-0 h-[2px] min-w-0 flex-1", n < current ? "bg-bm-placeholder" : active ? "bg-bm-ink" : "bg-bm-border")} />
            )}
            <span
              className={cx(
                "absolute left-[44px] top-[-20px] whitespace-nowrap",
                active ? "bm-h-xs text-bm-ink" : "bm-label-xs",
                done ? "text-bm-ink" : !active && "text-bm-muted",
              )}
            >
              {n}. {label}
            </span>
          </div>
        );
      })}
    </div>
  </Card>
);

// `compact` renders the phone list instead of the card strip.
export const DealStepper = ({ current = 1, className = "", compact = false }) =>
  compact ? (
    <DealStepperList current={current} className={className} />
  ) : (
    <DealStepperCard current={current} className={className} />
  );

const LIFECYCLE = [
  { label: "Draft", sub: "Built in Discover · shortlist and budget" },
  { label: "Open", sub: "Offers sent · awaiting athlete response" },
  { label: "In Contract", sub: "Terms agreed · signatures in progress" },
  { label: "In Progress", sub: "Signed · content and payouts running" },
  { label: "Completed", sub: "All deliverables approved and paid" },
];

// 5-step "How a campaign moves" explainer. `current` is 1-based.
export const LifecycleSteps = ({ current = 1 }) => (
  <div>
    <Overline>How a campaign moves</Overline>
    <div className="mt-[14px] grid grid-cols-5 gap-2">
      {LIFECYCLE.map((step, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        const last = i === LIFECYCLE.length - 1;
        return (
          <div key={step.label} className="flex min-w-0 flex-col gap-[6px]">
            <div className="flex items-center gap-[10px]">
              <span
                className={cx(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bm-label-xs",
                  done ? "bg-bm-orange text-white" : active ? "border-2 border-bm-orange bg-white text-bm-orange" : "bg-bm-mutedbg text-bm-tertiary",
                )}
              >
                {done ? "✓" : n}
              </span>
              {!last && <span className={cx("h-[2px] flex-1", done ? "bg-bm-orange" : "bg-bm-border")} />}
            </div>
            <div className={cx("bm-h-xs", done || active ? "text-bm-ink" : "text-bm-tertiary")}>{step.label}</div>
            <div className="bm-body-s text-bm-muted">{step.sub}</div>
          </div>
        );
      })}
    </div>
    <p className="mt-[14px] bm-body-s text-bm-tertiary">
      The moment you send offers, a draft becomes an active campaign and moves from the builder into deal
      management. Athletes are notified and the change cannot be undone.
    </p>
  </div>
);

// ------------------------------------------------------------------ Misc
export const EmptyState = ({ children, action, className = "" }) => (
  <div className={cx("flex flex-col items-center gap-3 rounded-[8px] border border-dashed border-bm-mutedbg bg-bm-subtle py-8 text-center", className)}>
    <p className="bm-body-m text-bm-muted">{children}</p>
    {action}
  </div>
);

export const Loading = ({ label = "Loading..." }) => (
  <div className="py-16 text-center bm-body-m text-bm-muted">{label}</div>
);

// A critical-tinted note on bm tokens. Extra props (role="alert", an id for
// aria-describedby, data attributes) land on the box.
export const ErrorState = ({ children, className = "", ...props }) => (
  <div
    className={cx("rounded-[8px] border border-bm-critical/20 bg-bm-critical/5 px-4 py-3 bm-body-m text-bm-critical", className)}
    {...props}
  >
    {children}
  </div>
);

// ErrorState's attention-tinted twin: something to know that is not a
// failure (data shown from a saved copy, for one). Extra props (role="status",
// an id, data attributes) land on the box.
export const NoticeState = ({ children, className = "", ...props }) => (
  <div className={cx("rounded-[8px] border border-bm-attention bg-bm-attention/15 px-4 py-3 bm-body-m text-bm-ink", className)} {...props}>
    {children}
  </div>
);

export const Dot = ({ color, className = "" }) => (
  <span className={cx("inline-block h-2 w-2 rounded-full", className)} style={{ background: color }} />
);

export const ChevronIcon = ({ className = "h-[7px] w-3" }) => <img src={iconChevron} alt="" className={className} />;

export const InfoButton = ({ onClick, label = "How a campaign moves" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="flex h-5 w-5 items-center justify-center rounded-full border border-bm-strong text-[11px] font-bold text-bm-muted hover:border-bm-ink hover:text-bm-ink"
  >
    i
  </button>
);
